<template>
  <transition name="job-category-picker-dialog" appear>
    <div
      v-if="open && featureEnabled"
      class="job-category-picker-dialog__overlay"
      :class="{ 'job-category-picker-dialog__overlay--stacked': stackAboveModal }"
      role="dialog"
      aria-modal="true"
      aria-labelledby="job-category-picker-dialog-title"
      @mousedown.self="handleDismiss"
    >
      <div class="job-category-picker-dialog__card">
        <div class="job-category-picker-dialog__header">
          <div class="job-category-picker-dialog__header-icon" aria-hidden="true">
            <JobCategoryIcon size="normal" />
          </div>
          <button
            type="button"
            class="job-category-picker-dialog__close-btn"
            aria-label="Close"
            @click="handleDismiss"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="job-category-picker-dialog__intro">
          <span id="job-category-picker-dialog-title" class="job-category-picker-dialog__title">
            {{ title }}
          </span>
          <span class="job-category-picker-dialog__subtitle">
            {{ subtitle }}
          </span>
        </div>

        <div class="job-category-picker-dialog__content">
          <JobCategoryPicker
            :model-value="localSelection"
            :results="results"
            :loading="loading"
            inline-results
            @update:modelValue="handleUpdateModelValue"
            @search="handleSearch"
          />
        </div>

        <div class="job-category-picker-dialog__footer">
          <p
            v-if="error"
            class="job-category-picker-dialog__error"
            role="alert"
            data-testid="job-category-picker-dialog-error"
          >
            {{ error }}
          </p>
          <div class="job-category-picker-dialog__footer-actions">
            <StandardButton
              type="magenta"
              variant="outline"
              size="full-width"
              label="Cancel"
              @click="handleDismiss"
            />
            <StandardButton
              type="magenta"
              variant="primary"
              size="full-width"
              :label="busy ? busyConfirmLabel : confirmLabel"
              :disabled="!canAssign"
              :loaderLeft="busy"
              @click="handleAssign"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import JobCategoryIcon from "@components/icons/items/OrgObject/JobCategoryIcon.vue";
import JobCategoryPicker from "@components/JobCategoryPicker/JobCategoryPicker.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { LD_FLAG_IDEA_MDP_566 } from "@components/constants/launchDarklyFlagKeys";

/**
 * JobCategoryPickerDialog — ER-12041. "Assign Job Category" modal for assigning one
 * Active Job Category to a Position. Chrome matches JobFamilyPickerDialog (featured
 * icon, title, subtitle, close ×, Cancel/Assign footer, busy state); body embeds
 * JobCategoryPicker. Opened from AssignedJobCategoryTag's assign event. Presentational:
 * caller fetches categories and performs the assignment. Behind the idea-MDP-566
 * kill switch shared with Job Categories.
 */
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Object,
    default: null,
  },
  results: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  busy: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Assign Job Category",
  },
  subtitle: {
    type: String,
    default: "Choose the job category for this position.",
  },
  confirmLabel: {
    type: String,
    default: "Assign",
  },
  busyConfirmLabel: {
    type: String,
    default: "Assigning...",
  },
  /** Raise overlay above nested Vuetify v-dialog modals. */
  stackAboveModal: {
    type: Boolean,
    default: false,
  },
  /** Inline server error (e.g. "Only Active job categories can be assigned.").
   *  Shown in the footer; the dialog stays open so the caller keeps it mounted
   *  while the user picks a different category. */
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:open", "update:modelValue", "search", "assign", "cancel"]);

const proxy = getCurrentInstance()?.proxy;
const featureEnabled = ref(true);

/** Local pick state (same pattern as JobFamilyPickerDialog) so Assign works even when the parent does not bind v-model. */
const localSelection = ref(null);

const canAssign = computed(() => !!localSelection.value?.jobCategoryId && !props.busy);

function hydrateLocalSelectionFromProps() {
  localSelection.value = props.modelValue?.jobCategoryId
    ? { jobCategoryId: props.modelValue.jobCategoryId }
    : null;
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      hydrateLocalSelectionFromProps();
      emit("search", "");
    } else {
      localSelection.value = null;
    }
  },
  { immediate: true }
);

watch(
  () => props.modelValue,
  (val) => {
    if (!props.open || !val?.jobCategoryId) return;
    localSelection.value = { jobCategoryId: val.jobCategoryId };
  }
);

function handleUpdateModelValue(value) {
  localSelection.value = value;
  emit("update:modelValue", value);
}

function handleSearch(query) {
  emit("search", query);
}

function handleDismiss() {
  emit("update:open", false);
  emit("cancel");
}

function handleAssign() {
  if (!canAssign.value) return;
  emit("assign", {
    jobCategoryId: localSelection.value.jobCategoryId,
  });
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.open) {
    handleDismiss();
  }
}

onMounted(async () => {
  document.addEventListener("keydown", handleKeydown);
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.job-category-picker-dialog-enter-active,
.job-category-picker-dialog-leave-active {
  transition: opacity 180ms ease;
}

.job-category-picker-dialog-enter-from,
.job-category-picker-dialog-leave-to {
  opacity: 0;
}

.job-category-picker-dialog-enter-active .job-category-picker-dialog__card,
.job-category-picker-dialog-leave-active .job-category-picker-dialog__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.job-category-picker-dialog-enter-from .job-category-picker-dialog__card,
.job-category-picker-dialog-leave-to .job-category-picker-dialog__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .job-category-picker-dialog-enter-active,
  .job-category-picker-dialog-leave-active,
  .job-category-picker-dialog-enter-active .job-category-picker-dialog__card,
  .job-category-picker-dialog-leave-active .job-category-picker-dialog__card {
    transition: none !important;
  }
}

.job-category-picker-dialog__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.job-category-picker-dialog__overlay--stacked {
  z-index: 10002;
}

.job-category-picker-dialog__card {
  width: 100%;
  max-width: 788px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  overflow: hidden;
}

.job-category-picker-dialog__header {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 0;
}

.job-category-picker-dialog__header-icon {
  display: flex;
  width: 48px;
  height: 48px;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  border: 1px solid #e9d7fe;
  background: #f9f5ff;
  box-shadow: 0 6px 16px -6px rgba(127, 86, 217, 0.25);
}

.job-category-picker-dialog__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 8px;
  margin: -4px -4px 0 0;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 8px;
}

.job-category-picker-dialog__close-btn:hover {
  background: rgba(152, 162, 179, 0.1);
}

.job-category-picker-dialog__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.job-category-picker-dialog__intro {
  display: flex;
  flex-direction: column;
  padding: 16px 24px 0;
}

.job-category-picker-dialog__title {
  color: #101828;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}

.job-category-picker-dialog__subtitle {
  color: #667085;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.job-category-picker-dialog__content {
  padding: 16px 24px 8px;
}

.job-category-picker-dialog__footer {
  margin-top: 16px;
  padding: 16px 24px 24px;
  border-top: 1px solid #e5e7eb;
}

.job-category-picker-dialog__error {
  margin: 0 0 12px;
  color: #b42318;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.job-category-picker-dialog__footer-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}

.job-category-picker-dialog__footer-actions :deep(.standard-button) {
  flex: 1 1 0;
  min-width: 0;
}

.job-category-picker-dialog__footer-actions :deep(.standard-button button) {
  width: 100%;
}
</style>
